/**
 * Primary account: choose UTC weekdays (Mon–Sun) when scheduled batch invoices may run.
 * Secondaries see the primary’s schedule read-only.
 */

import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { toast } from "@/components/ui/sonner";
import { getRegistrantAccountQueryKey } from "@/hera-api/client/@tanstack/react-query.gen";
import { updateRegistrantAccount } from "@/hera-api/client/sdk.gen";
import type {
  RegistrantAccountResponseSchema,
  UpdateRegistrantAccountRequestSchema,
} from "@/hera-api/client/types.gen";
import { getQueryErrorMessage } from "@/lib/utils";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { useEffect, useMemo, useState } from "react";

const UTC_WEEKDAY_OPTIONS: { value: number; label: string }[] = [
  { value: 0, label: "Mon" },
  { value: 1, label: "Tue" },
  { value: 2, label: "Wed" },
  { value: 3, label: "Thu" },
  { value: 4, label: "Fri" },
  { value: 5, label: "Sat" },
  { value: 6, label: "Sun" },
];

function sortedUniqueInts(values: Iterable<number>): number[] {
  return [...new Set(values)].sort((a, b) => a - b);
}

export interface InvoiceScheduleCardProps {
  account: RegistrantAccountResponseSchema;
}

export function InvoiceScheduleCard({ account }: InvoiceScheduleCardProps) {
  const queryClient = useQueryClient();
  const isSecondary = Boolean(account.primary_id);

  const [selected, setSelected] = useState<Set<number>>(() => {
    const days = account.invoice_auto_generate_weekdays;
    return new Set(Array.isArray(days) ? days : []);
  });

  useEffect(() => {
    const days = account.invoice_auto_generate_weekdays;
    setSelected(new Set(Array.isArray(days) ? days : []));
  }, [account.invoice_auto_generate_weekdays]);

  const saveMutation = useMutation({
    mutationFn: async (body: UpdateRegistrantAccountRequestSchema) => {
      const { data } = await updateRegistrantAccount({
        body,
        throwOnError: true,
      });
      return data;
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: getRegistrantAccountQueryKey(),
      });
      toast({
        title: "Saved",
        description: "Invoice weekday schedule updated",
        variant: "secondary",
      });
    },
    onError: (err: unknown) => {
      toast({
        title: "Error",
        description: getQueryErrorMessage(err),
        variant: "destructive",
      });
    },
  });

  const serverSet = useMemo(() => {
    const days = account.invoice_auto_generate_weekdays;
    return new Set(Array.isArray(days) ? days : []);
  }, [account.invoice_auto_generate_weekdays]);

  const isDirty = useMemo(() => {
    if (selected.size !== serverSet.size) {
      return true;
    }
    for (const v of selected) {
      if (!serverSet.has(v)) {
        return true;
      }
    }
    return false;
  }, [selected, serverSet]);

  const toggle = (day: number) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(day)) {
        next.delete(day);
      } else {
        next.add(day);
      }
      return next;
    });
  };

  const handleSave = () => {
    saveMutation.mutate({
      invoice_auto_generate_weekdays: sortedUniqueInts(selected),
    });
  };

  return (
    <Card className="mt-6">
      <CardContent className="space-y-4 pt-6">
        <div>
          <h4 className="font-medium">Automated invoice weekdays (UTC)</h4>
          <p className="text-sm text-muted-foreground mt-1">
            If your jurisdiction runs scheduled invoices, only the weekdays you
            select here are eligible. Times are UTC (for example, Monday 03:00
            UTC is Sunday evening in US Pacific). Invoice emails go to the{" "}
            <span className="font-medium text-foreground">
              accounts contact
            </span>{" "}
            set under{" "}
            <Link
              to="/registrant/dashboard"
              className="text-primary underline-offset-4 hover:underline"
            >
              Dashboard → Users
            </Link>
            .
          </p>
          {isSecondary && (
            <p className="text-sm text-muted-foreground mt-2">
              This schedule applies to your organization and is managed by the
              primary account holder.
            </p>
          )}
        </div>

        <div className="flex flex-wrap gap-4">
          {UTC_WEEKDAY_OPTIONS.map(({ value, label }) => {
            const id = `invoice-weekday-${value}`;
            return (
              <div key={value} className="flex items-center gap-2">
                <Checkbox
                  id={id}
                  checked={selected.has(value)}
                  disabled={isSecondary}
                  onCheckedChange={() => toggle(value)}
                />
                <Label
                  htmlFor={id}
                  className="text-sm font-normal cursor-pointer"
                >
                  {label}
                </Label>
              </div>
            );
          })}
        </div>

        {!isSecondary && (
          <div className="flex gap-2">
            <Button
              type="button"
              onClick={handleSave}
              disabled={!isDirty || saveMutation.isPending}
            >
              {saveMutation.isPending ? "Saving…" : "Save schedule"}
            </Button>
            <Button
              type="button"
              variant="outline"
              disabled={
                isSecondary || selected.size === 0 || saveMutation.isPending
              }
              onClick={() => setSelected(new Set())}
            >
              Clear selection
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
